import { useEffect, useMemo, useRef, useState } from "react";
import type { ClipboardEvent, FormEvent, KeyboardEvent } from "react";
import {
  authenticateProvisionedWorkspace,
  completePublicTenantProvisioning,
  resendPublicRegistration,
  startPublicRegistration,
  verifyPublicRegistration
} from "../api/public-signup-client";
import { createIdempotencyKey } from "../api/client";
import { SignupEcosystem } from "./SignupEcosystem";
import { SignupAccountStep, SignupVerificationStep } from "./SignupSteps";
import {
  moduleOrder,
  PRIVACY_VERSION,
  RESEND_COOLDOWN_SECONDS,
  TERMS_VERSION,
  uiCopy
} from "./signup-copy";
import type { Language, ModuleKey } from "./signup-copy";
import "./public-signup.css";
import "./signup-resilience.css";

type SignupStage = "account" | "verification" | "provisioning";

interface PublicSignupExperienceProps {
  onSignIn: () => void;
  onWorkspaceReady: () => void;
}

interface StableOperation {
  signature: string;
  key: string;
}

function operationKeyFor(
  reference: { current: StableOperation | null },
  signature: string
): string {
  if (reference.current?.signature === signature) {
    return reference.current.key;
  }
  const operation = { signature, key: createIdempotencyKey() };
  reference.current = operation;
  return operation.key;
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isStrongPassword(value: string): boolean {
  return value.length >= 10 && /\d/.test(value) && /[^A-Za-z0-9]/.test(value);
}

function localizedError(error: unknown, language: Language): string {
  const fallback = uiCopy[language].unexpected;
  if (!(error instanceof Error)) return fallback;

  const problem = error as Error & {
    problem?: { title?: string; detail?: string };
  };
  const code = problem.problem?.title ?? error.message;
  if (code === "SIGNUP_VERIFICATION_INVALID") {
    return language === "ar" ? "رمز التحقق غير صحيح." : "The verification code is incorrect.";
  }
  if (code === "SIGNUP_VERIFICATION_EXPIRED" || code === "SIGNUP_REGISTRATION_EXPIRED") {
    return language === "ar" ? "انتهت صلاحية الرمز. أعد إرسال رمز جديد." : "The code expired. Request a new one.";
  }
  if (code === "SIGNUP_VERIFICATION_BLOCKED") {
    return language === "ar" ? "تم إيقاف التحقق بعد محاولات متعددة. ابدأ التسجيل من جديد." : "Verification was locked after multiple attempts. Start again.";
  }
  if (code === "NEW_OWNER_CONTEXT_NOT_READY") {
    return uiCopy[language].contextDelay;
  }
  if (code === "PUBLIC_SIGNUP_DISABLED") {
    return language === "ar" ? "التسجيل العام غير متاح مؤقتًا." : "Public signup is temporarily unavailable.";
  }
  if (code === "SIGNUP_LEGAL_VERSION_OUTDATED") {
    return language === "ar"
      ? "تم تحديث الشروط أو سياسة الخصوصية. ارجع إلى إنشاء الحساب وراجع النسخ الحالية."
      : "The Terms or Privacy Policy changed. Return to account creation and review the current versions.";
  }
  return problem.problem?.detail || fallback;
}

export function PublicSignupExperience({
  onSignIn,
  onWorkspaceReady
}: PublicSignupExperienceProps) {
  const [language, setLanguage] = useState<Language>("ar");
  const [stage, setStage] = useState<SignupStage>("account");
  const [activeModule, setActiveModule] = useState<ModuleKey>("sales");
  const [email, setEmail] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [registrationId, setRegistrationId] = useState("");
  const [verificationCode, setVerificationCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [provisioningMessage, setProvisioningMessage] = useState("");
  const [handoffFailed, setHandoffFailed] = useState(false);
  const codeRefs = useRef<Array<HTMLInputElement | null>>([]);
  const startOperation = useRef<StableOperation | null>(null);
  const verifyOperation = useRef<StableOperation | null>(null);
  const completeOperation = useRef<StableOperation | null>(null);
  const verificationCompleted = useRef(false);
  const provisioningCompleted = useRef(false);
  const registrationAccessGrant = useRef("");
  const text = uiCopy[language];

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.title = language === "ar" ? "Leylion — إنشاء حساب" : "Leylion — Create account";
    return () => {
      document.documentElement.lang = "ar";
      document.documentElement.dir = "rtl";
    };
  }, [language]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveModule((current) => {
        const index = moduleOrder.indexOf(current);
        return moduleOrder[(index + 1) % moduleOrder.length];
      });
    }, 3600);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setTimeout(() => setResendSeconds((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  const joinedCode = useMemo(() => verificationCode.join(""), [verificationCode]);

  const resetContinuation = () => {
    verificationCompleted.current = false;
    provisioningCompleted.current = false;
    verifyOperation.current = null;
    completeOperation.current = null;
    registrationAccessGrant.current = "";
    setHandoffFailed(false);
    setProvisioningMessage("");
  };

  const submitAccount = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setNotice("");

    if (!isValidEmail(email)) {
      setError(text.invalidEmail);
      return;
    }
    if (!ownerName.trim() || !businessName.trim()) {
      setError(text.requiredNames);
      return;
    }
    if (!isStrongPassword(password)) {
      setError(text.weakPassword);
      return;
    }
    if (!accepted) {
      setError(text.acceptTerms);
      return;
    }

    const payload = {
      email: email.trim(),
      preferredLanguage: language,
      countryCode: null,
      termsVersion: TERMS_VERSION,
      privacyVersion: PRIVACY_VERSION
    } as const;
    const signature = JSON.stringify(payload);

    try {
      setBusy(true);
      const response = await startPublicRegistration(
        payload,
        operationKeyFor(startOperation, signature)
      );
      resetContinuation();
      setRegistrationId(response.registrationId);
      setStage("verification");
      setResendSeconds(RESEND_COOLDOWN_SECONDS);
      setVerificationCode(["", "", "", "", "", ""]);
      window.setTimeout(() => codeRefs.current[0]?.focus(), 80);
    } catch (caught) {
      setError(localizedError(caught, language));
    } finally {
      setBusy(false);
    }
  };

  const continueToWorkspace = async () => {
    const completePayload = {
      signupIntentId: registrationId,
      ownerDisplayName: ownerName.trim(),
      newAccountPassword: password,
      tradeName: businessName.trim()
    };
    const completeSignature = JSON.stringify(completePayload);

    setStage("provisioning");
    setBusy(true);
    setError("");
    setNotice("");
    setHandoffFailed(false);

    try {
      if (!provisioningCompleted.current) {
        setProvisioningMessage(text.preparingWorkspace);
        if (!registrationAccessGrant.current) {
          throw new Error("REGISTRATION_ACCESS_DENIED");
        }
        const provisioned = await completePublicTenantProvisioning(
          registrationId,
          registrationAccessGrant.current,
          completePayload,
          operationKeyFor(completeOperation, completeSignature)
        );
        if (provisioned.failureCode || String(provisioned.status).toLowerCase() === "failed") {
          throw new Error(provisioned.failureCode || "PROVISIONING_FAILED");
        }
        provisioningCompleted.current = true;
      }

      setProvisioningMessage(text.opening);
      await authenticateProvisionedWorkspace(email.trim(), password);
      setPassword("");
      onWorkspaceReady();
    } catch (caught) {
      setHandoffFailed(true);
      setError(localizedError(caught, language));
      setProvisioningMessage(
        provisioningCompleted.current
          ? language === "ar"
            ? "تم إنشاء مساحة العمل. تعذر فتحها مؤقتًا، ويمكنك إعادة المحاولة دون إنشاء حساب جديد."
            : "Your workspace was created. Opening it failed temporarily; retry without creating another account."
          : language === "ar"
            ? "لم تكتمل تهيئة مساحة العمل بعد. أعد المحاولة بنفس العملية الآمنة."
            : "Workspace provisioning is not complete yet. Retry the same safe operation."
      );
    } finally {
      setBusy(false);
    }
  };

  const submitVerification = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (!/^\d{6}$/.test(joinedCode)) {
      setError(text.enterCode);
      const emptyIndex = verificationCode.findIndex((digit) => !digit);
      codeRefs.current[emptyIndex < 0 ? 0 : emptyIndex]?.focus();
      return;
    }

    try {
      setBusy(true);
      if (!verificationCompleted.current) {
        const verifySignature = `${registrationId}:${joinedCode}`;
        const verified = await verifyPublicRegistration(
          registrationId,
          joinedCode,
          operationKeyFor(verifyOperation, verifySignature)
        );
        registrationAccessGrant.current = verified.accessGrant;
        verificationCompleted.current = true;
      }
    } catch (caught) {
      setError(localizedError(caught, language));
      setBusy(false);
      return;
    }

    setBusy(false);
    await continueToWorkspace();
  };

  const resendCode = async () => {
    if (!registrationId || resendSeconds > 0 || busy || verificationCompleted.current) return;
    setError("");
    setNotice("");
    try {
      setBusy(true);
      await resendPublicRegistration(registrationId, createIdempotencyKey());
      verifyOperation.current = null;
      setVerificationCode(["", "", "", "", "", ""]);
      setResendSeconds(RESEND_COOLDOWN_SECONDS);
      setNotice(text.resent);
    } catch (caught) {
      setError(localizedError(caught, language));
    } finally {
      setBusy(false);
    }
  };

  const updateCode = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setVerificationCode((current) => current.map((item, itemIndex) => itemIndex === index ? digit : item));
    setError("");
    if (digit && index < 5) codeRefs.current[index + 1]?.focus();
  };

  const handleCodeKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !verificationCode[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowLeft" && index < 5) codeRefs.current[index + 1]?.focus();
    if (event.key === "ArrowRight" && index > 0) codeRefs.current[index - 1]?.focus();
  };

  const handleCodePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!digits) return;
    event.preventDefault();
    const next = Array.from({ length: 6 }, (_, index) => digits[index] ?? "");
    setVerificationCode(next);
    codeRefs.current[Math.min(digits.length, 6) - 1]?.focus();
  };

  return (
    <section className="ss-page" dir={language === "ar" ? "rtl" : "ltr"}>
      <header className="ss-top">
        <div className="ss-brand" aria-label="Leylion">
          <span className="ss-mark" aria-hidden="true">
            <svg viewBox="0 0 42 42"><path d="M8.7 8.3h10.1v17.9c0 4 2.5 6.5 6.6 6.5h8.1v-9.6h-5.8V8.3h5.8V4H8.7z" /></svg>
          </span>
          <span className="ss-word">leylion</span>
        </div>
        <div className="ss-top-actions">
          <button className="ss-lang" type="button" onClick={() => setLanguage(language === "ar" ? "en" : "ar")}>{text.language}</button>
          <button className="ss-login" data-testid="public-signup-signin" type="button" onClick={onSignIn}>{text.signIn}</button>
        </div>
      </header>

      <main className="ss-layout">
        <section className="ss-copy">
          <div className="ss-eyebrow"><i /><span>{text.eyebrow}</span></div>
          <h1>{text.hero}</h1>
          <p className="ss-lead">{text.lead}</p>
          <SignupEcosystem language={language} activeModule={activeModule} onSelect={setActiveModule} />
        </section>

        <aside className="ss-card">
          {stage === "account" ? (
            <SignupAccountStep
              language={language}
              text={text}
              email={email}
              ownerName={ownerName}
              businessName={businessName}
              password={password}
              accepted={accepted}
              error={error}
              busy={busy}
              onEmailChange={setEmail}
              onOwnerNameChange={setOwnerName}
              onBusinessNameChange={setBusinessName}
              onPasswordChange={setPassword}
              onAcceptedChange={setAccepted}
              onSubmit={(event) => void submitAccount(event)}
              onSignIn={onSignIn}
            />
          ) : stage === "verification" ? (
            <SignupVerificationStep
              text={text}
              email={email}
              verificationCode={verificationCode}
              codeRefs={codeRefs}
              error={error}
              notice={notice}
              busy={busy}
              resendSeconds={resendSeconds}
              onSubmit={(event) => void submitVerification(event)}
              onCodeChange={updateCode}
              onCodeKeyDown={handleCodeKeyDown}
              onCodePaste={handleCodePaste}
              onResend={() => void resendCode()}
              onBack={() => {
                if (verificationCompleted.current) return;
                setStage("account");
                setError("");
                setNotice("");
              }}
            />
          ) : (
            <section className={`ss-prep${handoffFailed ? " is-retry" : ""}`} data-testid="public-signup-provisioning" aria-live="polite">
              {busy ? <div className="ss-loader" /> : <div className="ss-retry-mark" aria-hidden="true">↻</div>}
              <h2>{handoffFailed
                ? language === "ar" ? "مساحة عملك محفوظة" : "Your workspace progress is safe"
                : text.preparing}</h2>
              <p>{provisioningMessage || text.preparingWorkspace}</p>
              {error ? <div className="ss-form-error ss-prep-error" role="alert">{error}</div> : null}
              {busy ? (
                <div className="ss-provisioning-pulse"><span /><span /><span /></div>
              ) : (
                <div className="ss-prep-actions">
                  <button className="ss-btn ss-primary" data-testid="public-signup-retry-handoff" type="button" onClick={() => void continueToWorkspace()}>
                    {language === "ar" ? "إعادة المحاولة الآمنة" : "Retry safely"}
                  </button>
                  <button className="ss-btn ss-secondary" type="button" onClick={onSignIn}>
                    {language === "ar" ? "تسجيل الدخول بدلًا من ذلك" : "Sign in instead"}
                  </button>
                </div>
              )}
            </section>
          )}
        </aside>
      </main>
    </section>
  );
}